import { useState, useEffect } from "react";
import { AdminLayout } from "./AdminDashboard";
import API from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { ShoppingCart, Plus, Save, Trash2, Eye, EyeOff, Package, Coins, Upload } from "lucide-react";

export default function AdminShop() {
  const [packs, setPacks] = useState([]);
  const [coinPkgs, setCoinPkgs] = useState([]);
  const [tab, setTab] = useState("upgrades");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", price: 100, image_url: "", items: {}, enabled: true });
  const [coinForm, setCoinForm] = useState({ name: "", coins: 100, bonus: 0, price_usd: 1, enabled: true });
  const [showCreate, setShowCreate] = useState(false);
  const [showCoinCreate, setShowCoinCreate] = useState(false);
  const [editingCoin, setEditingCoin] = useState(null);
  const [loading, setLoading] = useState("");
  const [uploading, setUploading] = useState(false);
  const [flagged, setFlagged] = useState([]);
  const [sections, setSections] = useState({});
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    Promise.all([
      API.get("/admin/shop/packs").then(r => setPacks(r.data.packs)),
      API.get("/admin/coin-packages").then(r => setCoinPkgs(r.data.packages)),
      API.get("/admin/flagged-users").then(r => setFlagged(r.data.flagged)).catch(() => {}),
      API.get("/admin/section-toggles").then(r => setSections(r.data)).catch(() => {}),
      API.get("/admin/worker-tiers").then(r => setTiers(r.data.tiers)).catch(() => {}),
    ]).catch(() => {});
  }, []);

  const updateItems = (key, value) => {
    setForm(prev => ({ ...prev, items: { ...prev.items, [key]: value } }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(prev => ({ ...prev, image_url: data.url }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const create = async () => {
    setLoading("create");
    try {
      await API.post("/admin/shop/packs", form);
      toast.success("Pack created");
      const { data } = await API.get("/admin/shop/packs");
      setPacks(data.packs);
      setShowCreate(false);
      setForm({ name: "", description: "", price: 100, image_url: "", items: {}, enabled: true });
    } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  const save = async (packId) => {
    setLoading(`save-${packId}`);
    try {
      await API.put(`/admin/shop/packs/${packId}`, form);
      toast.success("Pack updated");
      const { data } = await API.get("/admin/shop/packs");
      setPacks(data.packs);
      setEditing(null);
    } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  const deletePack = async (packId) => {
    try {
      await API.delete(`/admin/shop/packs/${packId}`);
      toast.success("Deleted");
      setPacks(prev => prev.filter(p => p.pack_id !== packId));
    } catch { toast.error("Failed"); }
  };

  const ItemEditor = () => (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
      <div className="col-span-2 sm:col-span-2">
        <Label className="text-[#64748B] text-[10px]">Workers</Label>
        <div className="flex gap-1 mt-0.5">
          <Input type="number" value={form.items.workers || ""} onChange={(e) => updateItems("workers", parseInt(e.target.value) || 0)} placeholder="Qty" className="w-16 bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-7 text-xs" />
          <select value={form.items.worker_tier || "gold"} onChange={(e) => updateItems("worker_tier", e.target.value)}
            className="flex-1 h-7 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-1 text-xs"
            disabled={!form.items.workers}>
            {tiers.map(t => <option key={t.tier_id} value={t.tier_id}>{t.name} ({t.base_rate}/day, {t.cost}c)</option>)}
            {tiers.length === 0 && <option value="gold">Gold (default)</option>}
          </select>
        </div>
      </div>
      <div>
        <Label className="text-[#64748B] text-[10px]">Energy</Label>
        <Input type="number" value={form.items.energy || ""} onChange={(e) => updateItems("energy", parseInt(e.target.value) || 0)} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-7 text-xs" />
      </div>
      <div>
        <Label className="text-[#64748B] text-[10px]">Efficiency</Label>
        <Input type="number" step="0.1" value={form.items.efficiency || ""} onChange={(e) => updateItems("efficiency", parseFloat(e.target.value) || 0)} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-7 text-xs" />
      </div>
      <div>
        <Label className="text-[#64748B] text-[10px]">Bonus Coins</Label>
        <Input type="number" value={form.items.coins || ""} onChange={(e) => updateItems("coins", parseInt(e.target.value) || 0)} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-7 text-xs" />
      </div>
      {[
        { slot: "tool", label: "Tool", options: ["Steel Pickaxe", "Diamond Drill"] },
        { slot: "shoes", label: "Shoes", options: ["Speed Boots", "Gravity Boots"] },
        { slot: "machine", label: "Machine", options: ["Auto-Miner", "Quantum Excavator"] },
        { slot: "bag", label: "Bag", options: ["Large Bag", "Void Container"] },
      ].map(({ slot, label, options }) => (
        <div key={slot} className="col-span-1">
          <Label className="text-[#64748B] text-[10px]">{label}</Label>
          <div className="flex gap-1 mt-0.5">
            <select value={form.items[slot] || ""} onChange={(e) => updateItems(slot, e.target.value)}
              className="flex-1 h-7 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-1 text-xs">
              <option value="">None</option>
              {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <Input type="number" min="1" placeholder="Qty" value={form.items[`${slot}_qty`] || ""}
              onChange={(e) => updateItems(`${slot}_qty`, parseInt(e.target.value) || 1)}
              className="w-14 bg-[#0B0E14] border-[#2A3241] text-[#FFD600] h-7 text-xs text-center font-mono"
              disabled={!form.items[slot]} />
          </div>
        </div>
      ))}
    </div>
  );

  // ── Coin Package functions ──
  const createCoinPkg = async () => {
    setLoading("create-coin");
    try {
      await API.post("/admin/coin-packages", coinForm);
      toast.success("Coin package created");
      const { data } = await API.get("/admin/coin-packages");
      setCoinPkgs(data.packages);
      setShowCoinCreate(false);
      setCoinForm({ name: "", coins: 100, bonus: 0, price_usd: 1, enabled: true });
    } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  const saveCoinPkg = async (pkgId) => {
    setLoading(`save-coin-${pkgId}`);
    try {
      await API.put(`/admin/coin-packages/${pkgId}`, coinForm);
      toast.success("Updated");
      const { data } = await API.get("/admin/coin-packages");
      setCoinPkgs(data.packages);
      setEditingCoin(null);
    } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  const deleteCoinPkg = async (pkgId) => {
    try {
      await API.delete(`/admin/coin-packages/${pkgId}`);
      toast.success("Deleted");
      setCoinPkgs(prev => prev.filter(p => p.package_id !== pkgId));
    } catch { toast.error("Failed"); }
  };

  const toggleSection = async (key) => {
    const newVal = !sections[key];
    setSections(prev => ({ ...prev, [key]: newVal }));
    try {
      await API.put("/admin/section-toggles", { [key]: newVal });
      toast.success(`${key.replace("_enabled", "")} ${newVal ? "enabled" : "disabled"}`);
    } catch { toast.error("Failed"); }
  };

  const flagAction = async (userId, action) => {
    try {
      await API.post(`/admin/flagged-users/${userId}/action`, { action });
      toast.success(action === "ignore" ? "Dismissed" : "Marked suspicious");
      setFlagged(prev => action === "ignore" ? prev.filter(f => f.user_id !== userId) : prev.map(f => f.user_id === userId ? { ...f, status: "suspicious" } : f));
    } catch { toast.error("Failed"); }
  };

  return (
    <AdminLayout title="Shop Management">
      <div className="space-y-4" data-testid="admin-shop">
        {/* Tabs */}
        <div className="flex gap-1 bg-[#131722] rounded-lg p-1 border border-[#2A3241]/40">
          {[["upgrades", "Upgrade Packs"], ["coins", "Coin Packages"], ["flags", "Flagged Users"], ["toggles", "Section Toggles"]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`shop-admin-tab-${key}`}
              className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest rounded-md transition-all ${tab === key ? "bg-[#FFD600]/10 text-[#FFD600]" : "text-[#64748B] hover:text-[#94A3B8]"}`}>
              {label}
            </button>
          ))}
        </div>

        {/* ── UPGRADE PACKS TAB (existing, untouched) ── */}
        {tab === "upgrades" && (<>
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#94A3B8]">Create and manage upgrade packs for the shop.</p>
          <button onClick={() => setShowCreate(!showCreate)} data-testid="toggle-create-pack"
            className="flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90">
            <Plus className="w-3 h-3" /> New Pack
          </button>
        </div>

        {showCreate && (
          <div className="bg-[#131722] rounded-xl border border-[#00F0FF]/30 p-4 space-y-3" data-testid="create-pack-form">
            <h3 className="text-sm font-bold text-[#F8FAFC]">Create Pack</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label className="text-[#94A3B8] text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
              <div><Label className="text-[#94A3B8] text-xs">Price (coins)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) })} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
              <div><Label className="text-[#94A3B8] text-xs">Image URL</Label>
                <div className="flex gap-1 mt-1">
                  <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm flex-1" />
                  <label className="px-3 h-8 flex items-center gap-1 text-xs font-bold uppercase bg-[#B026FF]/10 text-[#B026FF] border border-[#B026FF]/30 rounded-lg hover:bg-[#B026FF]/20 cursor-pointer shrink-0">
                    <Upload className="w-3 h-3" />{uploading ? "..." : "Upload"}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                  </label>
                </div>
              </div>
            </div>
            <div><Label className="text-[#94A3B8] text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] text-sm mt-1" /></div>
            <div><Label className="text-[#94A3B8] text-xs">Pack Items</Label><ItemEditor /></div>
            <button onClick={create} disabled={loading === "create"} data-testid="create-pack-btn"
              className="px-4 py-2 text-xs font-bold uppercase bg-[#10B981] text-white rounded-lg disabled:opacity-50">
              {loading === "create" ? "Creating..." : "Create Pack"}
            </button>
          </div>
        )}

        <div className="space-y-3">
          {packs.map((pack) => (
            <div key={pack.pack_id} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4" data-testid={`admin-pack-${pack.pack_id}`}>
              <div className="flex items-center gap-4">
                {pack.image_url ? <img src={pack.image_url} alt="" className="w-16 h-16 rounded-lg object-cover border border-[#2A3241]" /> : <Package className="w-16 h-16 text-[#64748B]" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-[#F8FAFC]">{pack.name}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${pack.enabled ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#EF4444]/10 text-[#EF4444]"}`}>{pack.enabled ? "Active" : "Disabled"}</span>
                  </div>
                  <p className="text-xs text-[#94A3B8] truncate">{pack.description}</p>
                  <p className="font-mono-game text-xs text-[#FFD600] mt-1">{pack.price} coins</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => { setEditing(editing === pack.pack_id ? null : pack.pack_id); setForm({ name: pack.name, description: pack.description, price: pack.price, image_url: pack.image_url || "", items: pack.items || {}, enabled: pack.enabled }); }}
                    className="px-3 py-1.5 text-xs font-bold uppercase text-[#00F0FF] border border-[#00F0FF]/30 rounded-lg hover:bg-[#00F0FF]/10">Edit</button>
                  <button onClick={() => deletePack(pack.pack_id)} className="p-1.5 text-[#EF4444] hover:bg-[#EF4444]/10 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {editing === pack.pack_id && (
                <div className="mt-4 pt-4 border-t border-[#2A3241]/30 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div><Label className="text-[#94A3B8] text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                    <div><Label className="text-[#94A3B8] text-xs">Price</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) })} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                    <div><Label className="text-[#94A3B8] text-xs">Image URL</Label>
                      <div className="flex gap-1 mt-1">
                        <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm flex-1" />
                        <label className="px-3 h-8 flex items-center gap-1 text-xs font-bold uppercase bg-[#B026FF]/10 text-[#B026FF] border border-[#B026FF]/30 rounded-lg hover:bg-[#B026FF]/20 cursor-pointer shrink-0">
                          <Upload className="w-3 h-3" />{uploading ? "..." : "Upload"}
                          <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                        </label>
                      </div>
                    </div>
                  </div>
                  <div><Label className="text-[#94A3B8] text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] text-sm mt-1" /></div>
                  <div><Label className="text-[#94A3B8] text-xs">Pack Items</Label><ItemEditor /></div>
                  <div className="flex gap-2">
                    <button onClick={() => save(pack.pack_id)} disabled={loading === `save-${pack.pack_id}`} className="px-4 py-2 text-xs font-bold uppercase bg-[#10B981] text-white rounded-lg disabled:opacity-50"><Save className="w-3 h-3 inline mr-1" />Save</button>
                    <button onClick={() => setForm({ ...form, enabled: !form.enabled })} className={`px-3 py-2 text-xs font-bold uppercase rounded-lg border ${form.enabled ? "border-[#10B981]/30 text-[#10B981]" : "border-[#EF4444]/30 text-[#EF4444]"}`}>
                      {form.enabled ? <><Eye className="w-3 h-3 inline mr-1" />Enabled</> : <><EyeOff className="w-3 h-3 inline mr-1" />Disabled</>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        </>)}

        {/* ── COIN PACKAGES TAB ── */}
        {tab === "coins" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#94A3B8]">Manage coin purchase packages. Users buy with withdrawable balance.</p>
              <button onClick={() => setShowCoinCreate(!showCoinCreate)} data-testid="toggle-create-coin-pkg"
                className="flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-widest bg-[#FFD600] text-[#0B0E14] rounded-lg hover:bg-[#FFD600]/90">
                <Plus className="w-3 h-3" /> New Coin Package
              </button>
            </div>

            {showCoinCreate && (
              <div className="bg-[#131722] rounded-xl border border-[#FFD600]/30 p-4 space-y-3" data-testid="create-coin-pkg-form">
                <h3 className="text-sm font-bold text-[#F8FAFC] flex items-center gap-2"><Coins className="w-4 h-4 text-[#FFD600]" /> New Coin Package</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div><Label className="text-[#94A3B8] text-xs">Name</Label><Input value={coinForm.name} onChange={(e) => setCoinForm({...coinForm, name: e.target.value})} placeholder="500 Coins" className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                  <div><Label className="text-[#94A3B8] text-xs">Coins Amount</Label><Input type="number" value={coinForm.coins} onChange={(e) => setCoinForm({...coinForm, coins: parseInt(e.target.value) || 0})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                  <div><Label className="text-[#94A3B8] text-xs">Bonus Coins (optional)</Label><Input type="number" value={coinForm.bonus} onChange={(e) => setCoinForm({...coinForm, bonus: parseInt(e.target.value) || 0})} placeholder="0" className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                  <div><Label className="text-[#94A3B8] text-xs">Price (USD)</Label><Input type="number" step="0.01" value={coinForm.price_usd} onChange={(e) => setCoinForm({...coinForm, price_usd: parseFloat(e.target.value) || 0})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                </div>
                {coinForm.bonus > 0 && <p className="text-xs text-[#10B981]">Total coins on purchase: {coinForm.coins + coinForm.bonus} ({coinForm.coins} + {coinForm.bonus} bonus)</p>}
                <button onClick={createCoinPkg} disabled={loading === "create-coin"} data-testid="create-coin-pkg-btn"
                  className="px-4 py-2 text-xs font-bold uppercase bg-[#10B981] text-white rounded-lg disabled:opacity-50">Create</button>
              </div>
            )}

            {coinPkgs.map(pkg => (
              <div key={pkg.package_id} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4 flex items-center gap-4" data-testid={`coin-pkg-admin-${pkg.package_id}`}>
                <div className="w-12 h-12 rounded-full bg-[#FFD600]/10 flex items-center justify-center flex-shrink-0"><Coins className="w-6 h-6 text-[#FFD600]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-[#F8FAFC]">{pkg.name}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${pkg.enabled ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#EF4444]/10 text-[#EF4444]"}`}>{pkg.enabled ? "Active" : "Inactive"}</span>
                  </div>
                  <p className="text-xs text-[#94A3B8]">{pkg.coins} coins{pkg.bonus > 0 ? ` + ${pkg.bonus} bonus` : ""} &middot; ${pkg.price_usd?.toFixed(2)}</p>
                </div>
                <div className="flex items-center gap-1">
                  {editingCoin === pkg.package_id ? (
                    <>
                      <button onClick={() => saveCoinPkg(pkg.package_id)} disabled={!!loading} className="p-1.5 text-[#10B981] hover:bg-[#10B981]/10 rounded"><Save className="w-4 h-4" /></button>
                      <button onClick={() => setEditingCoin(null)} className="p-1.5 text-[#94A3B8] hover:bg-[#1C212E] rounded">Cancel</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => { setEditingCoin(pkg.package_id); setCoinForm({ name: pkg.name, coins: pkg.coins, bonus: pkg.bonus || 0, price_usd: pkg.price_usd, enabled: pkg.enabled }); }}
                        className="px-3 py-1.5 text-xs font-bold uppercase text-[#00F0FF] border border-[#00F0FF]/30 rounded-lg hover:bg-[#00F0FF]/10">Edit</button>
                      <button onClick={() => deleteCoinPkg(pkg.package_id)} className="p-1.5 text-[#EF4444] hover:bg-[#EF4444]/10 rounded"><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {editingCoin && (
              <div className="bg-[#131722] rounded-xl border border-[#00F0FF]/30 p-4 space-y-3">
                <h3 className="text-sm font-bold text-[#F8FAFC]">Edit: {coinForm.name}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div><Label className="text-[#94A3B8] text-xs">Name</Label><Input value={coinForm.name} onChange={(e) => setCoinForm({...coinForm, name: e.target.value})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                  <div><Label className="text-[#94A3B8] text-xs">Coins</Label><Input type="number" value={coinForm.coins} onChange={(e) => setCoinForm({...coinForm, coins: parseInt(e.target.value) || 0})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                  <div><Label className="text-[#94A3B8] text-xs">Bonus</Label><Input type="number" value={coinForm.bonus} onChange={(e) => setCoinForm({...coinForm, bonus: parseInt(e.target.value) || 0})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                  <div><Label className="text-[#94A3B8] text-xs">Price USD</Label><Input type="number" step="0.01" value={coinForm.price_usd} onChange={(e) => setCoinForm({...coinForm, price_usd: parseFloat(e.target.value) || 0})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                </div>
                <button onClick={() => setCoinForm({...coinForm, enabled: !coinForm.enabled})} className={`px-3 py-1.5 text-xs font-bold uppercase rounded-lg border ${coinForm.enabled ? "border-[#10B981]/30 text-[#10B981]" : "border-[#EF4444]/30 text-[#EF4444]"}`}>
                  {coinForm.enabled ? "Active" : "Inactive"}
                </button>
              </div>
            )}
            {coinPkgs.length === 0 && <p className="text-[#94A3B8] text-center py-6">No coin packages. Create one above.</p>}
          </div>
        )}

        {/* ── FLAGGED USERS TAB ── */}
        {tab === "flags" && (
          <div className="space-y-3">
            <p className="text-sm text-[#94A3B8]">Users flagged for suspicious activity (same IP multi-account, rapid purchases).</p>
            {flagged.length > 0 ? flagged.map(f => (
              <div key={f.user_id} className="bg-[#131722] rounded-xl border border-[#EF4444]/20 p-4 flex items-center gap-4" data-testid={`flagged-${f.user_id}`}>
                <div className="flex-1">
                  <p className="text-sm font-bold text-[#F8FAFC]">{f.username || f.user_id}</p>
                  <p className="text-xs text-[#64748B]">IP: {f.ip} | {f.reason}</p>
                  <p className="text-xs text-[#64748B]">{f.flagged_at ? new Date(f.flagged_at).toLocaleString() : ""} {f.status ? `| Status: ${f.status}` : ""}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => flagAction(f.user_id, "ignore")} className="px-3 py-1.5 text-xs font-bold uppercase text-[#94A3B8] border border-[#2A3241] rounded-lg hover:bg-[#1C212E]">Dismiss</button>
                  <button onClick={() => flagAction(f.user_id, "suspicious")} className="px-3 py-1.5 text-xs font-bold uppercase text-[#EF4444] border border-[#EF4444]/30 rounded-lg hover:bg-[#EF4444]/10">Suspicious</button>
                </div>
              </div>
            )) : <p className="text-[#94A3B8] text-center py-6">No flagged users</p>}
          </div>
        )}

        {/* ── SECTION TOGGLES TAB ── */}
        {tab === "toggles" && (
          <div className="space-y-3">
            <p className="text-sm text-[#94A3B8]">Enable or disable game sections. Disabled sections are hidden from users.</p>
            {[
              ["jobs_enabled", "Jobs Page (main tab)"],
              ["offerwalls_enabled", "Offerwalls (inside Jobs)"],
              ["ptc_enabled", "PTC Ads (inside Jobs)"],
              ["advertise_enabled", "Advertise (inside Jobs)"],
              ["tasks_enabled", "Tasks & Missions"],
              ["leaderboard_enabled", "Leaderboard"],
              ["faucetpay_deposits_enabled", "FaucetPay Deposits"],
              ["faucetpay_withdrawals_enabled", "FaucetPay Withdrawals"],
            ].map(([key, label]) => (
              <div key={key} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4 flex items-center justify-between" data-testid={`toggle-${key}`}>
                <span className="text-sm font-bold text-[#F8FAFC]">{label}</span>
                <button onClick={() => toggleSection(key)}
                  className={`px-4 py-2 text-xs font-bold uppercase rounded-lg border transition-all ${sections[key] ? "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]" : "border-[#EF4444]/40 bg-[#EF4444]/10 text-[#EF4444]"}`}>
                  {sections[key] ? "ENABLED" : "DISABLED"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
